import { ArrowRight, BookOpen, Clock } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import NotFound from "@/components/NotFound";
import { ClosingBand, Crumbs, PublicHeader, breadcrumbs, jsonLd } from "@/components/UseCasePage";
import { useHeadMeta } from "@/lib/head";
import { SITE_URL } from "@/content/site";
import { DOCS, DOCS_HUB, DOCS_UPDATED, DOCS_UPDATED_ISO, findDoc, pathForDoc } from "@/content/docs.mjs";
import "./HomepageFonts.css";
import "./Homepage.css";
import "./UseCases.css";

const HOME = { name: "Home", path: "/" };
const HUB = { name: "Docs", path: DOCS_HUB.path };

// Plain text with https:// URLs turned into links. Content is our own copy.
function Text({ children }) {
  return String(children).split(/(https:\/\/[^\s]*[^\s.,;:)])/g).map((part, i) => /^https:\/\//.test(part) ? <a key={i} href={part} rel="noopener noreferrer">{part}</a> : part);
}

export function DocsIndex() {
  useHeadMeta({ title: DOCS_HUB.title, robots: "index, follow" });
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "CollectionPage", "@id": `${SITE_URL}${DOCS_HUB.path}#webpage`, url: `${SITE_URL}${DOCS_HUB.path}`, name: DOCS_HUB.title, description: DOCS_HUB.description, inLanguage: "en-GB", isPartOf: { "@id": `${SITE_URL}/#website` }, publisher: { "@id": `${SITE_URL}/#organization` },
      hasPart: DOCS.map(d => ({ "@type": "TechArticle", url: `${SITE_URL}${pathForDoc(d.slug)}`, headline: d.h1 })) },
    breadcrumbs([HOME, HUB]),
  ] };
  return <div className="ss-home ss-uc">
    <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(graph)} />
    <a className="ss-skip" href="#main">Skip to content</a>
    <PublicHeader />
    <main id="main">
      <section className="ss-uc-hero"><div className="ss-container">
        <Crumbs items={[HOME, HUB]} />
        <p className="ss-eyebrow">{DOCS_HUB.eyebrow}</p>
        <h1>{DOCS_HUB.h1}</h1>
        <p className="ss-lead">{DOCS_HUB.lead}</p>
      </div></section>
      <section className="ss-container ss-uc-grid-wrap" aria-label="Guides">
        <ol className="ss-uc-grid ss-docs-grid">{DOCS.map((d, i) => <li key={d.slug}><a href={pathForDoc(d.slug)} className="ss-uc-card" data-testid={`doc-link-${d.slug}`}>
          <span className="ss-uc-card-eyebrow">{`GUIDE ${i + 1}`}</span>
          <h2>{d.h1}</h2>
          <p>{d.description}</p>
          <span className="ss-uc-card-more">Read the guide <ArrowRight size={16} aria-hidden="true" /></span>
        </a></li>)}</ol>
      </section>
      <ClosingBand />
    </main>
    <SiteFooter />
  </div>;
}

export default function DocsPage({ slug }) {
  const doc = findDoc(slug);
  useHeadMeta({ title: doc?.title, robots: doc ? "index, follow" : "noindex, nofollow" });
  if (!doc) return <NotFound />;
  const path = pathForDoc(doc.slug);
  const crumbs = [HOME, HUB, { name: doc.nav, path }];
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "TechArticle", "@id": `${SITE_URL}${path}#article`, url: `${SITE_URL}${path}`, headline: doc.h1, description: doc.description, inLanguage: "en-GB", dateModified: DOCS_UPDATED_ISO, isPartOf: { "@id": `${SITE_URL}/#website` }, about: { "@id": `${SITE_URL}/#software` }, author: { "@id": `${SITE_URL}/#organization` }, publisher: { "@id": `${SITE_URL}/#organization` } },
    breadcrumbs(crumbs),
  ] };
  const index = DOCS.findIndex(d => d.slug === doc.slug);
  const next = DOCS[index + 1];
  const related = doc.related.map(findDoc).filter(Boolean);
  return <div className="ss-home ss-uc">
    <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(graph)} />
    <a className="ss-skip" href="#main">Skip to content</a>
    <PublicHeader />
    <main id="main">
      <section className="ss-uc-hero"><div className="ss-container ss-docs-head">
        <Crumbs items={crumbs} />
        <p className="ss-eyebrow">{`GUIDE ${index + 1} OF ${DOCS.length}`}</p>
        <h1>{doc.h1}</h1>
        <p className="ss-lead">{doc.intro}</p>
        <p className="ss-docs-updated"><Clock size={14} aria-hidden="true" /><span>Updated <time dateTime={DOCS_UPDATED_ISO}>{DOCS_UPDATED}</time></span></p>
      </div></section>
      <div className="ss-container ss-docs-layout">
        <nav aria-label="Guides" className="ss-docs-nav">
          <p className="ss-docs-nav-title"><BookOpen size={16} aria-hidden="true" />Guides</p>
          <ol>{DOCS.map(d => <li key={d.slug}><a href={pathForDoc(d.slug)} aria-current={d.slug === doc.slug ? "page" : undefined}>{d.nav}</a></li>)}</ol>
        </nav>
        <article className="ss-docs-article">
          {doc.sections.map((section, i) => <section key={section.heading} aria-labelledby={`doc-section-${i}`}>
            <h2 id={`doc-section-${i}`}>{section.heading}</h2>
            {section.steps && <ol className="ss-docs-steps">{section.steps.map(step => <li key={step}><Text>{step}</Text></li>)}</ol>}
            {section.list && <ul className="ss-docs-list">{section.list.map(item => <li key={item}><Text>{item}</Text></li>)}</ul>}
            {(section.body || []).map(p => <p key={p}><Text>{p}</Text></p>)}
          </section>)}
          {next && <a className="ss-docs-next" href={pathForDoc(next.slug)}><span>Next guide</span><strong>{next.h1}</strong><ArrowRight size={18} aria-hidden="true" /></a>}
          <aside className="ss-docs-related" aria-labelledby="doc-related">
            <h2 id="doc-related">Related guides</h2>
            <ul>{related.map(r => <li key={r.slug}><a href={pathForDoc(r.slug)}>{r.h1}</a></li>)}<li><a href={DOCS_HUB.path}>All guides</a></li></ul>
          </aside>
        </article>
      </div>
      <ClosingBand />
    </main>
    <SiteFooter />
  </div>;
}
